import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subscription, map } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { workspacePersonaFeature, WorkspacePersonaFetch } from '@workspaces/store/workspace-persona.feature';
import { WorkspacePersonaService } from '@workspaces/services/persona.service';
import { LoaderButtonComponent } from '../loader-button/loader-button.component';
import { PersonaSelectorComponent } from '../persona-selector/persona-selector.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-persona-set',
  templateUrl: './persona-set.component.html',
  styleUrls: ['./persona-set.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, LoaderButtonComponent, PersonaSelectorComponent]
})
export class PersonaSetComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public personaIsLoading$!: Observable<boolean>;
  public isPersonaSaving = false;
  public selectedPersonaIds: string[] = [];

  constructor(
    private readonly store: Store<AppState>,
    private readonly dialogRef: MatDialogRef<PersonaSetComponent>,
    private readonly workspacePersonaService: WorkspacePersonaService,
    private readonly snackbar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public readonly workspaceId: string
  ) {}

  ngOnInit(): void {
    this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.workspaceId }));

    // We only need the IDs for the selector.
    // We subscribe to workspace personas to get the initial set.
    this.sub.add(
      this.store
        .select(workspacePersonaFeature.workspacePersonaData(this.workspaceId))
        .pipe(map(wps => wps.map(wp => wp.persona_id).filter((id): id is string => !!id)))
        .subscribe(ids => {
          this.selectedPersonaIds = [...ids];
        })
    );

    this.personaIsLoading$ = this.store.select(workspacePersonaFeature.workspacePersonaIsLoading(this.workspaceId));
  }

  onSelectionChange(ids: string[]): void {
    this.selectedPersonaIds = ids;
  }

  onConfirm(): void {
    this.isPersonaSaving = true;

    this.sub.add(
      this.workspacePersonaService
        .setWorkspacePersonas(this.workspaceId, {
          persona_ids: this.selectedPersonaIds
        })
        .subscribe(() => {
          this.snackbar.open('Persona for the workspace was updated!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
          this.dialogRef.close();
        })
        .add(() => {
          this.isPersonaSaving = false;
          this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.workspaceId }));
        })
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
